import React, { useEffect, useState } from 'react';
import AdminLayout from '@/components/AdminLayout';
import { PageHeader } from '@/components/AIBadge';
import api, { apiErrorMessage } from '@/lib/api';
import { toast } from 'sonner';
import { Package as PackageIcon, Loader2, Check, X, Sparkles, Plus, Trash2, Edit3 } from 'lucide-react';

export default function AdminPackages() {
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [editing, setEditing] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get('/packages');
            setPackages(r.data || []);
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al cargar paquetes'));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); }, []);

    return (
        <AdminLayout title="Paquetes">
            <div className="max-w-[1200px] space-y-6">
                <PageHeader
                    icon={PackageIcon}
                    title="Paquetes de Distintivos"
                    subtitle="Configura los 3 paquetes disponibles. El multiplicador se aplica sobre el precio base del rubro para calcular el precio por auditoría."
                    accent="#7C6BEF"
                    data-testid="admin-packages-header"
                />

                {loading ? (
                    <div className="p-12 text-center opacity-60">
                        <Loader2 className="w-6 h-6 animate-spin mx-auto" />
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {packages.map((p) => (
                            <PackageCard key={p.code} pkg={p} onEdit={() => setEditing(p)} />
                        ))}
                    </div>
                )}

                <div className="p-4 border-l-4 text-xs flex items-start gap-2"
                    style={{ background: 'rgba(245,158,11,0.06)', borderColor: '#F59E0B' }}>
                    <Sparkles className="w-4 h-4 shrink-0 mt-0.5" style={{ color: '#F59E0B' }} />
                    <div>
                        <b>Nota:</b> los paquetes definen únicamente el multiplicador y las recomendaciones IA por auditoría.
                        Los precios finales se calculan combinando el precio base del rubro con el multiplicador del paquete elegido por el cliente.
                    </div>
                </div>
            </div>

            {editing && (
                <EditPackageModal
                    pkg={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); load(); }}
                />
            )}
        </AdminLayout>
    );
}

function PackageCard({ pkg, onEdit }) {
    const unlimited = pkg.ai_recommendations_per_audit === -1;
    return (
        <div className="p-5 border-2 relative"
            style={{ borderColor: pkg.color, background: `${pkg.color}08` }}
            data-testid={`pkg-card-${pkg.code}`}>
            {pkg.is_popular && (
                <div className="absolute -top-2 right-4 text-[9px] font-bold px-2 py-0.5 uppercase tracking-widest text-white"
                    style={{ background: pkg.color }}>
                    ★ Más elegido
                </div>
            )}
            <div className="flex items-center gap-2 mb-2">
                <PackageIcon className="w-5 h-5" style={{ color: pkg.color }} />
                <span className="text-[10px] uppercase tracking-widest font-bold opacity-60">{pkg.code}</span>
            </div>
            <div className="text-3xl font-display font-black mb-1" style={{ color: pkg.color }}>{pkg.name}</div>
            <p className="text-xs opacity-70 mb-4">{pkg.description}</p>

            <div className="grid grid-cols-2 gap-2 mb-4">
                <div className="p-2 border" style={{ borderColor: 'var(--border)' }}>
                    <div className="text-[9px] uppercase tracking-widest opacity-60">Multiplicador</div>
                    <div className="text-xl font-black">×{pkg.price_multiplier}</div>
                </div>
                <div className="p-2 border" style={{ borderColor: 'var(--border)' }}>
                    <div className="text-[9px] uppercase tracking-widest opacity-60">IA / auditoría</div>
                    <div className="text-xl font-black">{unlimited ? '∞' : pkg.ai_recommendations_per_audit}</div>
                </div>
            </div>

            <div className="space-y-1.5 mb-4">
                {pkg.features.map((f, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                        <Check className="w-3.5 h-3.5 shrink-0 mt-0.5" style={{ color: pkg.color }} />
                        <span>{f}</span>
                    </div>
                ))}
            </div>

            <button onClick={onEdit}
                data-testid={`edit-pkg-${pkg.code}`}
                className="w-full text-white text-sm font-bold py-2 inline-flex items-center justify-center gap-1"
                style={{ background: pkg.color }}>
                <Edit3 className="w-3.5 h-3.5" /> Editar paquete
            </button>
        </div>
    );
}

function EditPackageModal({ pkg, onClose, onSaved }) {
    const [form, setForm] = useState({
        name: pkg.name,
        description: pkg.description,
        color: pkg.color,
        price_multiplier: pkg.price_multiplier,
        ai_recommendations_per_audit: pkg.ai_recommendations_per_audit,
        features: [...(pkg.features || [])],
        is_popular: pkg.is_popular,
    });
    const [saving, setSaving] = useState(false);

    const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
    const addFeature = () => set('features', [...form.features, '']);
    const updFeature = (i, v) => {
        const nx = [...form.features]; nx[i] = v; set('features', nx);
    };
    const rmFeature = (i) => set('features', form.features.filter((_, idx) => idx !== i));

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/packages/${pkg.code}`, {
                ...form,
                price_multiplier: parseFloat(form.price_multiplier),
                ai_recommendations_per_audit: parseInt(form.ai_recommendations_per_audit),
                features: form.features.filter((f) => f.trim()),
            });
            toast.success('Paquete actualizado');
            onSaved();
        } catch (e) {
            toast.error(apiErrorMessage(e, 'Error al guardar'));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{ background: 'rgba(0,0,0,0.55)' }} onClick={onClose}>
            <div className="max-w-2xl w-full max-h-[92vh] overflow-hidden flex flex-col border"
                style={{ background: 'var(--card-bg)', borderColor: 'var(--border)' }}
                onClick={(e) => e.stopPropagation()}
                data-testid="edit-package-modal">
                <div className="p-5 border-b flex items-center justify-between shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest font-bold opacity-60">Editar paquete · {pkg.code}</div>
                        <div className="font-display font-black text-2xl" style={{ color: form.color }}>{form.name}</div>
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-black/5"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 overflow-y-auto space-y-4 flex-1 min-h-0">
                    <Field label="Nombre" value={form.name} onChange={(v) => set('name', v)} testid="pkg-name" />
                    <Field label="Descripción" textarea value={form.description} onChange={(v) => set('description', v)} testid="pkg-desc" />
                    <div className="grid grid-cols-3 gap-3">
                        <Field label="Color" type="color" value={form.color} onChange={(v) => set('color', v)} testid="pkg-color" />
                        <Field label="Multiplicador" type="number" step="0.1" value={form.price_multiplier} onChange={(v) => set('price_multiplier', v)} testid="pkg-mult" />
                        <Field label="IA/auditoría (-1=∞)" type="number" step="1" value={form.ai_recommendations_per_audit} onChange={(v) => set('ai_recommendations_per_audit', v)} testid="pkg-recs" />
                    </div>
                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.is_popular}
                            onChange={(e) => set('is_popular', e.target.checked)}
                            data-testid="pkg-popular" />
                        Marcar como {'"'}Más elegido{'"'} (destacado en la vista del cliente)
                    </label>

                    <div>
                        <div className="flex items-center justify-between mb-2">
                            <label className="text-[10px] uppercase tracking-widest font-bold opacity-60">Características incluidas</label>
                            <button onClick={addFeature}
                                data-testid="add-feature-btn"
                                className="text-xs font-bold inline-flex items-center gap-1 px-2 py-1 border-2"
                                style={{ borderColor: 'var(--border)' }}>
                                <Plus className="w-3 h-3" /> Añadir
                            </button>
                        </div>
                        <div className="space-y-2">
                            {form.features.map((f, i) => (
                                <div key={i} className="flex gap-2">
                                    <input type="text" value={f}
                                        onChange={(e) => updFeature(i, e.target.value)}
                                        data-testid={`feature-input-${i}`}
                                        className="flex-1 border-2 p-2 text-sm outline-none focus:border-black"
                                        style={{ borderColor: 'var(--border)', background: 'transparent' }} />
                                    <button onClick={() => rmFeature(i)}
                                        data-testid={`feature-rm-${i}`}
                                        className="p-2 border-2 hover:bg-red-500 hover:text-white hover:border-red-500"
                                        style={{ borderColor: 'var(--border)' }}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="p-5 border-t flex justify-end gap-2 shrink-0" style={{ borderColor: 'var(--border)' }}>
                    <button onClick={onClose}
                        className="px-4 py-2 border-2 text-sm font-bold"
                        style={{ borderColor: 'var(--border)' }}>Cancelar</button>
                    <button onClick={save} disabled={saving}
                        data-testid="save-package-btn"
                        className="px-4 py-2 text-white text-sm font-bold inline-flex items-center gap-1 disabled:opacity-50"
                        style={{ background: form.color }}>
                        {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
                        Guardar
                    </button>
                </div>
            </div>
        </div>
    );
}

function Field({ label, value, onChange, textarea, type = 'text', step, testid }) {
    return (
        <div>
            <label className="text-[10px] uppercase tracking-widest font-bold opacity-60 mb-1 block">{label}</label>
            {textarea ? (
                <textarea value={value} onChange={(e) => onChange(e.target.value)}
                    rows={3}
                    data-testid={testid}
                    className="w-full border-2 p-2 text-sm outline-none focus:border-black"
                    style={{ borderColor: 'var(--border)', background: 'transparent' }} />
            ) : (
                <input type={type} step={step} value={value}
                    onChange={(e) => onChange(e.target.value)}
                    data-testid={testid}
                    className="w-full border-2 p-2 text-sm outline-none focus:border-black"
                    style={{ borderColor: 'var(--border)', background: type === 'color' ? value : 'transparent' }} />
            )}
        </div>
    );
}
